import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { X, CreditCard, Tag, ClockCounterClockwise, FloppyDisk } from "@phosphor-icons/react";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
};
const inr = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

export default function PlatformBillingModal({ org, onClose, onSaved }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    // Subscription form
    const [plan, setPlan] = useState("");
    const [seats, setSeats] = useState("");
    const [cycle, setCycle] = useState("");
    const [extendDays, setExtendDays] = useState("");
    const [paidUntil, setPaidUntil] = useState("");
    const [trialDays, setTrialDays] = useState("");

    // Pricing form
    const [priceEssential, setPriceEssential] = useState("");
    const [priceProfessional, setPriceProfessional] = useState("");
    const [discountPct, setDiscountPct] = useState("");
    const [inrWa, setInrWa] = useState({ starter: "", base: "", growth: "", enterprise: "" });
    // USD custom pricing
    const [usdEssential, setUsdEssential] = useState("");
    const [usdProfessional, setUsdProfessional] = useState("");
    const [usdWa, setUsdWa] = useState({ starter: "", base: "", growth: "", enterprise: "" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/platform/orgs/${org.id}/billing`);
            setData(data);
            setPlan(data.org.plan || "essential");
            setSeats(String(data.org.seats || ""));
            setCycle(data.org.billing_cycle || "monthly");
            const cp = data.custom_pricing || {};
            setPriceEssential(cp.prices?.essential ? String(cp.prices.essential) : "");
            setPriceProfessional(cp.prices?.professional ? String(cp.prices.professional) : "");
            setDiscountPct(cp.discount_pct != null ? String(cp.discount_pct) : "");
            const wi = cp.whatsapp || {};
            setInrWa({ starter: wi.starter ? String(wi.starter) : "", base: wi.base ? String(wi.base) : "",
                       growth: wi.growth ? String(wi.growth) : "", enterprise: wi.enterprise ? String(wi.enterprise) : "" });
            setUsdEssential(cp.prices_usd?.essential ? String(cp.prices_usd.essential) : "");
            setUsdProfessional(cp.prices_usd?.professional ? String(cp.prices_usd.professional) : "");
            const wu = cp.whatsapp_usd || {};
            setUsdWa({ starter: wu.starter ? String(wu.starter) : "", base: wu.base ? String(wu.base) : "",
                       growth: wu.growth ? String(wu.growth) : "", enterprise: wu.enterprise ? String(wu.enterprise) : "" });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    }, [org.id]);

    useEffect(() => { load(); }, [load]);

    const saveSubscription = async () => {
        setBusy(true);
        try {
            const payload = { plan, seats: Number(seats) || undefined, billing_cycle: cycle };
            if (extendDays) payload.extend_days = Number(extendDays);
            if (paidUntil) payload.paid_until = new Date(paidUntil).toISOString();
            if (trialDays !== "") payload.trial_days = Number(trialDays);
            await api.put(`/platform/orgs/${org.id}/subscription`, payload);
            toast.success(`Subscription updated for ${org.name}`);
            setExtendDays(""); setPaidUntil(""); setTrialDays("");
            await load();
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const clearPaid = async () => {
        if (!window.confirm(`Revoke paid access for "${org.name}"? They will lose access unless in trial/comp.`)) return;
        setBusy(true);
        try {
            await api.put(`/platform/orgs/${org.id}/subscription`, { clear_paid: true });
            toast.success("Paid access revoked");
            await load();
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const savePricing = async () => {
        setBusy(true);
        try {
            const prices = {};
            if (priceEssential) prices.essential = Number(priceEssential);
            if (priceProfessional) prices.professional = Number(priceProfessional);
            const wa = {};
            ["starter", "base", "growth", "enterprise"].forEach((t) => { if (inrWa[t]) wa[t] = Number(inrWa[t]); });
            const payload = {};
            if (Object.keys(prices).length) payload.prices = prices;
            if (Object.keys(wa).length) payload.whatsapp = wa;
            if (discountPct !== "") payload.discount_pct = Number(discountPct);
            const pricesUsd = {};
            if (usdEssential) pricesUsd.essential = Number(usdEssential);
            if (usdProfessional) pricesUsd.professional = Number(usdProfessional);
            const waUsdPayload = {};
            ["starter", "base", "growth", "enterprise"].forEach((t) => { if (usdWa[t]) waUsdPayload[t] = Number(usdWa[t]); });
            if (Object.keys(pricesUsd).length) payload.prices_usd = pricesUsd;
            if (Object.keys(waUsdPayload).length) payload.whatsapp_usd = waUsdPayload;
            if (!payload.prices && !payload.whatsapp && payload.discount_pct == null
                && !payload.prices_usd && !payload.whatsapp_usd) {
                toast.error("Enter a custom price (INR or USD), a WhatsApp price, or a discount %");
                setBusy(false);
                return;
            }
            const { data } = await api.put(`/platform/orgs/${org.id}/pricing`, payload);
            setData((d) => ({ ...d, pricing: data.pricing, custom_pricing: data.custom_pricing,
                whatsapp_pricing: data.whatsapp_pricing || d.whatsapp_pricing,
                pricing_usd: data.pricing_usd || d.pricing_usd,
                whatsapp_pricing_usd: data.whatsapp_pricing_usd || d.whatsapp_pricing_usd }));
            toast.success("Custom pricing saved — applies at their checkout");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const clearPricing = async () => {
        setBusy(true);
        try {
            const { data } = await api.put(`/platform/orgs/${org.id}/pricing`, { clear: true });
            setPriceEssential(""); setPriceProfessional(""); setDiscountPct("");
            setInrWa({ starter: "", base: "", growth: "", enterprise: "" });
            setUsdEssential(""); setUsdProfessional(""); setUsdWa({ starter: "", base: "", growth: "", enterprise: "" });
            setData((d) => ({ ...d, custom_pricing: null,
                pricing: data.pricing || d.pricing,
                whatsapp_pricing: data.whatsapp_pricing || d.whatsapp_pricing,
                pricing_usd: data.pricing_usd || d.pricing_usd,
                whatsapp_pricing_usd: data.whatsapp_pricing_usd || d.whatsapp_pricing_usd }));
            toast.success("Reverted to standard pricing");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const setWhatsApp = async (state) => {
        setBusy(true);
        try {
            await api.post(`/platform/orgs/${org.id}/whatsapp`, { state });
            toast.success(state === "on" ? "WhatsApp enabled for this workspace" : state === "off" ? "WhatsApp disabled for this workspace" : "WhatsApp now follows billing");
            await load();
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const setCalling = async (state) => {
        setBusy(true);
        try {
            await api.post(`/platform/orgs/${org.id}/calling`, { state });
            toast.success(state === "on" ? "Calling enabled for this workspace" : state === "off" ? "Calling disabled for this workspace" : "Calling now follows billing");
            await load();
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    const o = data?.org;
    const pr = data?.pricing;

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4" data-testid="platform-billing-modal">
            <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-xl">
                <div className="flex items-start justify-between border-b border-stone-200 px-6 py-4">
                    <div className="flex items-center gap-2">
                        <CreditCard size={22} weight="duotone" className="text-blue-600" />
                        <div>
                            <div className="font-heading text-lg font-bold">Billing · {org.name}</div>
                            <div className="text-xs text-stone2">Manage this workspace's subscription & pricing</div>
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="platform-billing-close" className="rounded-full p-1.5 text-stone2 hover:bg-stone-100">
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
                    {loading && <div className="py-10 text-center text-stone2" data-testid="platform-billing-loading">Loading…</div>}

                    {!loading && o && (
                        <>
                            {/* Current state */}
                            <div className="grid grid-cols-2 gap-3 rounded-xl border border-stone-200 bg-stone-50 p-4 sm:grid-cols-4">
                                <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Plan</div><div className="mt-1 font-semibold capitalize" data-testid="billing-cur-plan">{o.plan}</div></div>
                                <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Seats</div><div className="mt-1 font-semibold">{o.seats || "—"}</div></div>
                                <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Cycle</div><div className="mt-1 font-semibold capitalize">{o.billing_cycle || "—"}</div></div>
                                <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone2">MRR</div><div className="mt-1 font-semibold">{inr(o.monthly_value)}</div></div>
                                <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Paid until</div><div className="mt-1 font-semibold">{fmtDate(o.paid_until)}</div></div>
                                <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Trial ends</div><div className="mt-1 font-semibold">{fmtDate(o.trial_ends_at)}</div></div>
                                <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Comp until</div><div className="mt-1 font-semibold">{fmtDate(o.comp_until)}</div></div>
                                <div><div className="text-[10px] font-bold uppercase tracking-widest text-stone2">Status</div><div className="mt-1 font-semibold capitalize">{o.subscription_status || "—"}</div></div>
                            </div>

                            {/* Manual subscription override */}
                            <div>
                                <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                    <FloppyDisk size={14} weight="duotone" /> Manual subscription (no payment)
                                </div>
                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                                    <label className="text-xs font-semibold text-stone2">Plan
                                        <select data-testid="billing-plan-select" value={plan} onChange={(e) => setPlan(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-ink outline-none focus:border-ink">
                                            <option value="essential">Essential</option>
                                            <option value="professional">Professional</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                    </label>
                                    <label className="text-xs font-semibold text-stone2">Seats
                                        <input data-testid="billing-seats-input" type="number" min="1" value={seats} onChange={(e) => setSeats(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                    </label>
                                    <label className="text-xs font-semibold text-stone2">Cycle
                                        <select data-testid="billing-cycle-select" value={cycle} onChange={(e) => setCycle(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-ink outline-none focus:border-ink">
                                            <option value="monthly">Monthly</option>
                                            <option value="yearly">Yearly</option>
                                        </select>
                                    </label>
                                    <label className="text-xs font-semibold text-stone2">Extend paid by (days)
                                        <input data-testid="billing-extend-input" type="number" min="1" placeholder="e.g. 30" value={extendDays} onChange={(e) => setExtendDays(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                    </label>
                                    <label className="text-xs font-semibold text-stone2">Or set paid-until
                                        <input data-testid="billing-paiduntil-input" type="date" value={paidUntil} onChange={(e) => setPaidUntil(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                    </label>
                                    <label className="text-xs font-semibold text-stone2">Set trial (days)
                                        <input data-testid="billing-trial-input" type="number" min="0" placeholder="e.g. 7" value={trialDays} onChange={(e) => setTrialDays(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                    </label>
                                </div>
                                <div className="mt-3 flex items-center gap-2">
                                    <button data-testid="billing-save-subscription" disabled={busy} onClick={saveSubscription} className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Save subscription</button>
                                    <button data-testid="billing-clear-paid" disabled={busy} onClick={clearPaid} className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40">Revoke paid access</button>
                                </div>
                            </div>

                            {/* Custom pricing */}
                            <div>
                                <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                    <Tag size={14} weight="duotone" /> Custom pricing (applies at their checkout)
                                </div>
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <label className="text-xs font-semibold text-stone2">Essential ₹/seat/mo
                                        <input data-testid="pricing-essential-input" type="number" min="1" placeholder={`List ${pr?.essential?.list ?? ""}`} value={priceEssential} onChange={(e) => setPriceEssential(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                    </label>
                                    <label className="text-xs font-semibold text-stone2">Professional ₹/seat/mo
                                        <input data-testid="pricing-professional-input" type="number" min="1" placeholder={`List ${pr?.professional?.list ?? ""}`} value={priceProfessional} onChange={(e) => setPriceProfessional(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                    </label>
                                    <label className="text-xs font-semibold text-stone2">Or discount %
                                        <input data-testid="pricing-discount-input" type="number" min="0" max="90" placeholder="e.g. 15" value={discountPct} onChange={(e) => setDiscountPct(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                    </label>
                                </div>
                                {pr && (
                                    <div className="mt-2 text-xs text-stone2" data-testid="pricing-effective">
                                        Effective now — Essential {inr(pr.essential?.effective)} · Professional {inr(pr.professional?.effective)}
                                    </div>
                                )}

                                {/* WhatsApp per-workspace pricing (INR, per volume tier) */}
                                <div className="mt-4 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 p-4" data-testid="wa-pricing-block">
                                    <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#128C4B]">WhatsApp price (₹/tier/month)</div>
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                        {["starter", "base", "growth", "enterprise"].map((t) => (
                                            <label key={t} className="text-xs font-semibold capitalize text-stone2">{t}
                                                <input data-testid={`pricing-wa-${t}-input`} type="number" min="1" placeholder={`₹${data?.whatsapp_pricing?.list?.[t] ?? ""}`} value={inrWa[t]} onChange={(e) => setInrWa((w) => ({ ...w, [t]: e.target.value }))} className="mt-1 w-full rounded-lg border border-stone-300 px-2 py-2 text-sm outline-none focus:border-ink" />
                                            </label>
                                        ))}
                                    </div>
                                    {data?.whatsapp_pricing && (
                                        <div className="mt-2 text-xs text-stone2" data-testid="wa-pricing-effective">
                                            Effective now — Starter {inr(data.whatsapp_pricing.effective?.starter)} · Base {inr(data.whatsapp_pricing.effective?.base)} · Growth {inr(data.whatsapp_pricing.effective?.growth)} · Enterprise {inr(data.whatsapp_pricing.effective?.enterprise)}
                                        </div>
                                    )}
                                    <p className="mt-1 text-[11px] text-stone2">Flat monthly price for each WhatsApp volume tier. Standalone adds a fixed surcharge on top; yearly auto-applies the standard discount. Reflects in their checkout &amp; billing page.</p>
                                </div>

                                {/* USD (international) custom pricing */}
                                <div className="mt-4 rounded-xl border border-blue-300/50 bg-blue-50/60 p-4" data-testid="usd-pricing-block">
                                    <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-blue-700">$ USD price overrides (international customers)</div>
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <label className="text-xs font-semibold text-stone2">Essential $/seat/mo
                                            <input data-testid="pricing-usd-essential-input" type="number" min="1" placeholder={`List $${data?.pricing_usd?.essential?.list ?? ""}`} value={usdEssential} onChange={(e) => setUsdEssential(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                        </label>
                                        <label className="text-xs font-semibold text-stone2">Professional $/seat/mo
                                            <input data-testid="pricing-usd-professional-input" type="number" min="1" placeholder={`List $${data?.pricing_usd?.professional?.list ?? ""}`} value={usdProfessional} onChange={(e) => setUsdProfessional(e.target.value)} className="mt-1 w-full rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none focus:border-ink" />
                                        </label>
                                    </div>
                                    <div className="mt-3 text-[11px] font-bold uppercase tracking-widest text-[#128C4B]">WhatsApp $/tier/mo</div>
                                    <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4">
                                        {["starter", "base", "growth", "enterprise"].map((t) => (
                                            <label key={t} className="text-xs font-semibold capitalize text-stone2">{t}
                                                <input data-testid={`pricing-usd-wa-${t}-input`} type="number" min="1" placeholder={`$${data?.whatsapp_pricing_usd?.list?.[t] ?? ""}`} value={usdWa[t]} onChange={(e) => setUsdWa((w) => ({ ...w, [t]: e.target.value }))} className="mt-1 w-full rounded-lg border border-stone-300 px-2 py-2 text-sm outline-none focus:border-ink" />
                                            </label>
                                        ))}
                                    </div>
                                    {data?.pricing_usd && (
                                        <div className="mt-2 text-xs text-stone2" data-testid="usd-pricing-effective">
                                            Effective USD — Essential ${data.pricing_usd.essential?.effective} · Professional ${data.pricing_usd.professional?.effective} · WA Base ${data.whatsapp_pricing_usd?.effective?.base}
                                        </div>
                                    )}
                                    <p className="mt-1 text-[11px] text-stone2">Leave blank to use the standard USD list price. Only applies when the customer pays in USD.</p>
                                </div>

                                <div className="mt-3 flex items-center gap-2">
                                    <button data-testid="pricing-save" disabled={busy} onClick={savePricing} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Save pricing</button>
                                    <button data-testid="pricing-clear" disabled={busy} onClick={clearPricing} className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-40">Reset to standard</button>
                                </div>
                            </div>

                            {/* Calling access override */}
                            <div data-testid="calling-override-section">
                                <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                    <Tag size={14} weight="duotone" /> Calling access
                                </div>
                                <div className="rounded-xl border border-stone-200 p-4">
                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                                        <span>Status: <b data-testid="calling-status" className={o.products?.calling?.active ? "text-emerald-600" : "text-stone-500"}>{o.products?.calling?.active ? "Enabled" : "Disabled"}</b></span>
                                        <span className="text-stone2">Override: <b className="capitalize">{o.calling_override || "auto"}</b></span>
                                        {o.products?.calling?.status && <span className="text-stone2">Plan state: <b className="capitalize">{o.products.calling.status}</b></span>}
                                    </div>
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <button data-testid="calling-enable" disabled={busy || o.calling_override === "on"} onClick={() => setCalling("on")} className="rounded-lg bg-ink px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Force enable</button>
                                        <button data-testid="calling-disable" disabled={busy || o.calling_override === "off"} onClick={() => setCalling("off")} className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40">Force disable</button>
                                        <button data-testid="calling-auto" disabled={busy || !o.calling_override || o.calling_override === "auto"} onClick={() => setCalling("auto")} className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-40">Follow billing (auto)</button>
                                    </div>
                                    <p className="mt-2 text-xs text-stone2">Force enable grants complimentary calling regardless of payment. Auto follows the paid plan / trial.</p>
                                </div>
                            </div>

                            {/* WhatsApp Messaging override */}
                            <div data-testid="wa-override-section">
                                <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                    <Tag size={14} weight="duotone" /> WhatsApp Messaging access
                                </div>
                                <div className="rounded-xl border border-stone-200 p-4">
                                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                                        <span>Status: <b data-testid="wa-status" className={o.whatsapp_enabled ? "text-emerald-600" : "text-stone-500"}>{o.whatsapp_enabled ? "Enabled" : "Disabled"}</b></span>
                                        <span className="text-stone2">Override: <b className="capitalize">{o.whatsapp_override || "auto"}</b></span>
                                        {o.whatsapp_mode && <span className="text-stone2">Tier: <b className="capitalize">{o.whatsapp_mode}</b></span>}
                                        {o.whatsapp_paid_until && <span className="text-stone2">Paid until: <b>{fmtDate(o.whatsapp_paid_until)}</b></span>}
                                    </div>
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                        <button data-testid="wa-enable" disabled={busy || o.whatsapp_override === "on"} onClick={() => setWhatsApp("on")} className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Force enable</button>
                                        <button data-testid="wa-disable" disabled={busy || o.whatsapp_override === "off"} onClick={() => setWhatsApp("off")} className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40">Force disable</button>
                                        <button data-testid="wa-auto" disabled={busy || !o.whatsapp_override || o.whatsapp_override === "auto"} onClick={() => setWhatsApp("auto")} className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-600 transition-colors hover:bg-stone-100 disabled:opacity-40">Follow billing (auto)</button>
                                    </div>
                                    <p className="mt-2 text-xs text-stone2">Force enable grants complimentary WhatsApp regardless of payment. Auto follows the paid add-on / standalone subscription.</p>
                                </div>
                            </div>

                            {/* Payments history */}
                            <div>
                                <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                    <ClockCounterClockwise size={14} weight="duotone" /> Payment history
                                </div>
                                {(data.payments || []).length === 0 ? (
                                    <div className="rounded-xl border border-dashed border-stone-300 p-4 text-sm text-stone2" data-testid="billing-no-payments">No payments yet for this workspace.</div>
                                ) : (
                                    <div className="overflow-hidden rounded-xl border border-stone-200">
                                        <table className="w-full text-left text-xs">
                                            <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone2">
                                                <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Plan</th><th className="px-3 py-2">Seats</th><th className="px-3 py-2">Amount</th><th className="px-3 py-2">Status</th></tr>
                                            </thead>
                                            <tbody className="divide-y divide-stone-100">
                                                {data.payments.map((p) => (
                                                    <tr key={p.order_id} data-testid={`payment-row-${p.order_id}`}>
                                                        <td className="px-3 py-2">{fmtDate(p.paid_at || p.created_at)}</td>
                                                        <td className="px-3 py-2 capitalize">{p.plan} · {p.cycle}</td>
                                                        <td className="px-3 py-2">{p.seats}</td>
                                                        <td className="px-3 py-2 font-semibold">{inr(p.amount)}</td>
                                                        <td className="px-3 py-2">
                                                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.status === "paid" ? "bg-emerald-100 text-emerald-700" : p.status === "failed" ? "bg-red-100 text-red-700" : "bg-stone-100 text-stone-500"}`}>{p.status}</span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
